import type { PortAreaExpansion, PortAreaExpansionResponse } from "@/types/api";

/** Result of classifying a result port against the requested origin(s). */
export interface AreaMatch {
  kind: "exact" | "area" | "none";
  /** For `area` matches, the requested LOCODE whose area the port belongs to. */
  requested?: string;
}

/**
 * Precomputed lookup structures derived from a port area-expansion response.
 * Build once per search and reuse for every rendered row.
 */
export interface AreaMatcher {
  /** True when at least one requested port expanded to extra area siblings. */
  hasExpansion: boolean;
  /** Upper-cased set of the originally requested LOCODEs. */
  requestedSet: Set<string>;
  /** Sibling LOCODE (not itself requested) -> requested LOCODE it came from. */
  memberToRequested: Map<string, string>;
  /** Raw per-requested-port expansions (for the banner). */
  expansions: PortAreaExpansion[];
}

const upper = (s: string): string => (s || "").trim().toUpperCase();

/** Display label for a port: "Name (LOCODE)" or just "LOCODE". */
export function portLabel(
  locode: string,
  name: string | null | undefined,
): string {
  return name && name.length > 0 ? `${name} (${locode})` : locode;
}

/** Build the reusable matcher from an area-expansion response (or null). */
export function buildAreaMatcher(
  data: PortAreaExpansionResponse | null | undefined,
): AreaMatcher {
  const requestedSet = new Set<string>();
  const memberToRequested = new Map<string, string>();
  let hasExpansion = false;
  const expansions = data?.expansions ?? [];

  for (const exp of expansions) {
    requestedSet.add(upper(exp.requested));
  }
  for (const exp of expansions) {
    const req = upper(exp.requested);
    for (const m of exp.members) {
      const loc = upper(m.locode);
      if (loc === req) continue;
      hasExpansion = true;
      if (!requestedSet.has(loc) && !memberToRequested.has(loc)) {
        memberToRequested.set(loc, req);
      }
    }
  }
  return { hasExpansion, requestedSet, memberToRequested, expansions };
}

/** Merge matchers from multiple lane origins (e.g. calendar with N lanes). */
export function mergeAreaMatchers(matchers: AreaMatcher[]): AreaMatcher {
  const requestedSet = new Set<string>();
  const memberToRequested = new Map<string, string>();
  const expansions: PortAreaExpansion[] = [];
  const seenRequested = new Set<string>();

  for (const matcher of matchers) {
    for (const loc of matcher.requestedSet) {
      requestedSet.add(loc);
    }
    for (const [loc, req] of matcher.memberToRequested) {
      if (!memberToRequested.has(loc)) {
        memberToRequested.set(loc, req);
      }
    }
    for (const exp of matcher.expansions) {
      const key = upper(exp.requested);
      if (seenRequested.has(key)) continue;
      seenRequested.add(key);
      expansions.push(exp);
    }
  }

  let hasExpansion = false;
  for (const exp of expansions) {
    const req = upper(exp.requested);
    if (exp.members.some((m) => upper(m.locode) !== req)) {
      hasExpansion = true;
      break;
    }
  }

  return { hasExpansion, requestedSet, memberToRequested, expansions };
}

/** Classify a single result LOCODE against the requested origin(s). */
export function classifyAreaMatch(
  matcher: AreaMatcher,
  locode: string | null | undefined,
): AreaMatch {
  const loc = upper(locode ?? "");
  if (!loc) return { kind: "none" };
  if (matcher.requestedSet.has(loc)) return { kind: "exact" };
  const req = matcher.memberToRequested.get(loc);
  if (req) return { kind: "area", requested: req };
  return { kind: "none" };
}
